import { useEffect, useRef, useState } from 'react';
import { useVsjFlow } from './vsj-flow';
import { MOCK_PLACES, MockPlace } from './vsj-mocks';
import { VsjBrandMark, IconSearch, IconPin } from './vsj-icons';
import { fetchGeocodeSuggestions, GeocodeResultDto } from './vsj-geocode-api';

type FetchStatus = 'idle' | 'loading' | 'error';

function resultToMockPlace(r: GeocodeResultDto): MockPlace {
  return {
    id:           `geo-${r.lng.toFixed(4)}_${r.lat.toFixed(4)}`,
    address:      r.address || r.label,
    street:       r.address || r.label,
    block:        '',
    neighborhood: r.neighborhood,
    city:         r.city || 'San Juan',
    lat:          r.lat,
    lng:          r.lng,
  };
}

export function VsjHome(): JSX.Element {
  const { searchQuery, setSearchQuery, selectPlace, goTo } = useVsjFlow();
  const [focused, setFocused]   = useState(false);
  const [results, setResults]   = useState<GeocodeResultDto[]>([]);
  const [status, setStatus]     = useState<FetchStatus>('idle');

  const inputRef    = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);
  const abortRef    = useRef<AbortController | null>(null);

  // ── Debounced geocode ───────────────────────────────────────────────────────

  useEffect(() => {
    if (debounceRef.current !== null) clearTimeout(debounceRef.current);

    const q = searchQuery.trim();
    if (q.length < 3) {
      setResults([]);
      setStatus('idle');
      return;
    }

    setStatus('loading');

    debounceRef.current = window.setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      fetchGeocodeSuggestions(q, ctrl.signal)
        .then((data) => {
          if (ctrl.signal.aborted) return;
          setResults(data.results);
          setStatus('idle');
        })
        .catch(() => {
          if (ctrl.signal.aborted) return;
          setResults([]);
          setStatus('error');
        });
    }, 350);

    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [searchQuery]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  function choose(p: MockPlace) {
    selectPlace(p);
    setSearchQuery(p.address);
    setFocused(false);
    goTo('location');
  }

  function handleCta() {
    if (results.length > 0) {
      choose(resultToMockPlace(results[0]));
    } else {
      inputRef.current?.focus();
      setFocused(true);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') handleCta();
    if (e.key === 'Escape') setFocused(false);
  }

  // ── Panel visibility flags ──────────────────────────────────────────────────

  const query             = searchQuery.trim();
  const showExamples      = query.length < 3;
  const showLoading       = !showExamples && status === 'loading';
  const showResults       = !showExamples && status !== 'loading' && results.length > 0;
  const showNoResults     = !showExamples && status === 'idle' && results.length === 0;
  const showErrorFallback = status === 'error';

  return (
    <div className="vsj-step vsj-home">
      <div className="vsj-home-inner">
        <header className="vsj-home-brand">
          <VsjBrandMark size={22} className="vsj-home-brand-mark" />
          <span className="vsj-home-brand-label">Valor SJ</span>
        </header>

        <section className="vsj-home-hero">
          <h1 className="vsj-home-title">
            Entendé lo que tu propiedad vale — y por qué.
          </h1>
          <p className="vsj-home-thesis">
            <span>Primero comprendemos el lugar.</span>
            <br />
            Después hablamos de valor.
          </p>

          <div className="vsj-home-search-group">
            <div className="vsj-search-wrapper">
              <IconSearch size={18} className="vsj-search-icon" />
              <input
                ref={inputRef}
                className="vsj-search-input"
                type="text"
                placeholder="Escribí la dirección de la propiedad"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => window.setTimeout(() => setFocused(false), 140)}
                onKeyDown={handleKeyDown}
                aria-label="Dirección de la propiedad"
                autoComplete="off"
                autoCorrect="off"
              />

              {focused && (
                <div className="vsj-suggest-panel" role="listbox">

                  {showLoading && (
                    <p className="vsj-suggest-hint">Buscando…</p>
                  )}

                  {showNoResults && (
                    <p className="vsj-suggest-hint">
                      Sin resultados para esa dirección en San Juan.
                    </p>
                  )}

                  {(showExamples || showErrorFallback) && (
                    <>
                      <p className="vsj-suggest-hint">
                        {showErrorFallback
                          ? 'No se pudo conectar al geocoder. Probá con una de estas:'
                          : 'Probá con una de estas direcciones'}
                      </p>
                      {MOCK_PLACES.map((p) => (
                        <button
                          key={p.id}
                          role="option"
                          aria-selected={false}
                          className="vsj-suggest-item"
                          onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                        >
                          <IconPin size={17} className="vsj-suggest-pin" />
                          <span className="vsj-suggest-text">
                            <span className="vsj-suggest-main">{p.address}</span>
                            <span className="vsj-suggest-sub">{p.neighborhood}, {p.city}</span>
                          </span>
                        </button>
                      ))}
                    </>
                  )}

                  {showResults && (
                    <>
                      <p className="vsj-suggest-hint">Direcciones en San Juan</p>
                      {results.map((r) => (
                        <button
                          key={`${r.lng.toFixed(4)}_${r.lat.toFixed(4)}`}
                          role="option"
                          aria-selected={false}
                          className="vsj-suggest-item"
                          onMouseDown={(e) => { e.preventDefault(); choose(resultToMockPlace(r)); }}
                        >
                          <IconPin size={17} className="vsj-suggest-pin" />
                          <span className="vsj-suggest-text">
                            <span className="vsj-suggest-main">{r.address}</span>
                            <span className="vsj-suggest-sub">
                              {[r.neighborhood, r.city].filter(Boolean).join(', ')}
                            </span>
                          </span>
                        </button>
                      ))}
                    </>
                  )}

                </div>
              )}
            </div>

            <button className="vsj-btn-primary" onClick={handleCta}>
              Ubicar la propiedad
            </button>
          </div>
        </section>

        <footer className="vsj-home-footer">
          San Juan, Argentina · Acceso anticipado
        </footer>
      </div>
    </div>
  );
}
